import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { FaBoxOpen, FaClipboardList, FaUsers, FaWallet } from "react-icons/fa6";
import { HiTrendingUp } from "react-icons/hi";
import { Link } from 'react-router-dom';
import SummaryApi from '../common';
import displayNPRCurrency from '../helpers/displayCurrency';
import { request } from '../helpers/request';

const AdminDashboard = () => {
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalProducts: 0,
        totalOrders: 0,
        totalRevenue: 0,
        recentOrders: [],
        categoryStats: [],
        lowStockProducts: []
    });

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await request(SummaryApi.dashboardStats);
                const data = await response.json();
                if (data.success) {
                    setStats(data.data);
                }
            } catch (error) {
                console.error("Failed to fetch dashboard stats", error);
            }
        };
        fetchStats();
    }, []);

    const StatCard = ({ title, value, icon, color, subtext }) => (
        <div className='bg-white p-5 md:p-6 rounded-[1.5rem] md:rounded-[2rem] border border-surface-border shadow-sm hover:shadow-premium transition-all group'>
            <div className='flex items-center justify-between mb-3 md:mb-4'>
                <div className={`w-12 h-12 md:w-14 md:h-14 rounded-xl md:rounded-2xl flex items-center justify-center text-white text-xl md:text-2xl shadow-lg ${color}`}>
                    {icon}
                </div>
                {subtext && <span className='text-[10px] md:text-xs font-bold text-green-500 bg-green-50 px-2 py-1 rounded-full flex items-center gap-1'><HiTrendingUp /> {subtext}</span>}
            </div>
            <div>
                <p className='text-gray-400 font-black text-[10px] md:text-xs uppercase tracking-widest mb-1'>{title}</p>
                <h3 className='text-2xl md:text-3xl font-black text-primary'>{value}</h3>
            </div>
        </div>
    );

    return (
        <div className='space-y-6 md:space-y-8'>
             <div className='space-y-2'>
                <h1 className='text-3xl md:text-5xl font-black text-primary tracking-tight'>Dashboard</h1>
                <p className='text-xs md:text-base text-gray-500 font-medium'>Store performance at a glance.</p>
            </div>

            {/* Stats Grid */}
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6'>
                <StatCard 
                    title="Revenue" 
                    value={displayNPRCurrency(stats.totalRevenue)} 
                    icon={<FaWallet />} 
                    color="bg-gradient-to-br from-green-400 to-green-600"
                    subtext="12% up"
                />
                <StatCard 
                    title="Orders" 
                    value={stats.totalOrders} 
                    icon={<FaClipboardList />} 
                    color="bg-gradient-to-br from-blue-400 to-blue-600"
                />
                <StatCard 
                    title="Users" 
                    value={stats.totalUsers} 
                    icon={<FaUsers />} 
                    color="bg-gradient-to-br from-purple-400 to-purple-600"
                />
                <StatCard 
                    title="Products" 
                    value={stats.totalProducts} 
                    icon={<FaBoxOpen />} 
                    color="bg-gradient-to-br from-orange-400 to-orange-600"
                />
            </div>

            {/* Recent Orders & Quick Actions */}
            <div className='grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8'>
                {/* Recent Orders */}
                <div className='lg:col-span-2 bg-white rounded-[2rem] md:rounded-[2.5rem] p-5 md:p-8 border border-surface-border shadow-sm'>
                   <div className='flex items-center justify-between mb-6 md:mb-8'>
                        <h3 className='text-base md:text-xl font-black text-primary'>Recent Activity</h3>
                        <Link to="all-orders" className='text-[10px] md:text-sm font-black text-accent uppercase tracking-widest hover:underline'>View Full Report</Link>
                   </div>
                   
                   <div className='overflow-x-auto -mx-5 px-5 md:mx-0 md:px-0 scrollbar-none'>
                        <table className='w-full min-w-[500px]'>
                            <thead>
                                <tr className='text-left border-b border-surface-border'>
                                    <th className='pb-4 text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest'>ID</th>
                                    <th className='pb-4 text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest'>Customer</th>
                                    <th className='pb-4 text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest text-right'>Amount</th>
                                    <th className='pb-4 text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest text-center'>Status</th>
                                </tr>
                            </thead>
                            <tbody className='divide-y divide-surface-border/30'>
                                {stats.recentOrders.map((order, idx) => (
                                    <tr key={idx} className='group hover:bg-surface-muted/30 transition-colors'>
                                        <td className='py-4 md:py-5 font-mono font-bold text-primary text-xs group-hover:text-accent transition-colors'>
                                            #{order?._id?.slice(-8).toUpperCase()}
                                        </td>
                                        <td className='py-4 md:py-5'>
                                            <div className='flex flex-col'>
                                                <span className='text-xs md:text-sm font-bold text-primary leading-tight'>{order?.userId?.name || "Guest"}</span>
                                                <span className='text-[10px] text-gray-400 font-medium'>{order?.userId?.email || "No email"}</span>
                                            </div>
                                        </td>
                                        <td className='py-4 md:py-5 text-right'>
                                            <span className='font-black text-primary text-xs md:text-sm'>{displayNPRCurrency(order?.totalAmount)}</span>
                                        </td>
                                        <td className='py-4 md:py-5 text-center'>
                                            <span className={`text-[8px] md:text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full inline-block
                                                ${order.paymentDetails.payment_status === 'paid' ? 'bg-green-100/50 text-green-600' : 'bg-yellow-100/50 text-yellow-600'}
                                            `}>
                                                {order.paymentDetails.payment_status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                                {stats.recentOrders.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className='py-12 text-center text-gray-400 font-medium text-sm italic'>No recent orders found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                   </div>
                </div>

                {/* Quick Stats / Categories */}
                <div className='bg-white rounded-[2rem] md:rounded-[2.5rem] p-6 md:p-8 border border-surface-border shadow-sm h-fit'>
                    <div className='mb-8'>
                        <h3 className='text-base md:text-xl font-black text-primary mb-1'>Category Share</h3>
                        <p className='text-[10px] md:text-xs text-gray-400 font-bold uppercase tracking-widest'>Inventory Breakdown</p>
                    </div>
                    <div className='space-y-5 md:space-y-6'>
                        {stats.categoryStats.map((cat, idx) => {
                            const percentage = (cat.count / stats.totalProducts) * 100;
                            return (
                                <div key={idx} className='space-y-2'>
                                    <div className='flex items-center justify-between'>
                                        <span className='font-bold text-xs md:text-sm text-gray-600 capitalize'>{cat._id}</span>
                                        <span className='text-[11px] md:text-xs font-black text-primary'>{cat.totalStock} Units</span>
                                    </div>
                                    <div className='w-full h-2 md:h-2.5 bg-surface-muted rounded-full overflow-hidden'>
                                        <motion.div 
                                            initial={{ width: 0 }}
                                            whileInView={{ width: `${percentage}%` }}
                                            transition={{ duration: 1, ease: "easeOut" }}
                                            className='h-full bg-gradient-to-r from-accent to-accent/60 rounded-full' 
                                        />
                                    </div>
                                </div>
                            );
                        })}
                         {stats.categoryStats.length === 0 && (
                            <div className='py-12 text-center text-gray-400 font-medium text-sm border-2 border-dashed border-surface-border rounded-2xl'>
                                No category data available
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Low Stock Alerts */}
            {stats.lowStockProducts.length > 0 && (
                <div className='bg-white rounded-[2rem] md:rounded-[2.5rem] p-6 md:p-8 border border-surface-border shadow-sm'>
                    <div className='flex items-center justify-between mb-6 md:mb-8'>
                        <div>
                            <h3 className='text-base md:text-xl font-black text-primary'>Low Stock Alerts</h3>
                            <p className='text-[10px] md:text-xs text-red-500 font-bold uppercase tracking-widest'>Action Required Immediately</p>
                        </div>
                        <Link to="/admin-panel/all-products" className='px-4 py-2 bg-slate-100 text-[10px] md:text-xs font-black uppercase tracking-widest rounded-xl hover:bg-slate-200 transition-all'>Restock Items</Link>
                    </div>

                    <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4'>
                        {stats.lowStockProducts.map((product, idx) => (
                            <div key={idx} className='p-4 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-between group hover:border-red-200 hover:bg-red-50/30 transition-all'>
                                <div className='flex flex-col'>
                                    <span className='text-sm font-bold text-primary truncate max-w-[150px] md:max-w-[200px]'>{product.productName}</span>
                                    <span className='text-[10px] text-gray-400 font-bold uppercase tracking-widest'>
                                        {product.hasVariants ? "Multiple Variants" : "Standard Product"}
                                    </span>
                                </div>
                                <div className='flex flex-col items-end'>
                                    <span className={`text-xs font-black px-2 py-1 rounded-lg ${
                                        (product.hasVariants ? Math.min(...product.variants.map(v => v.stock)) : product.stock) <= 0 
                                        ? 'bg-red-500 text-white' 
                                        : 'bg-red-100 text-red-600'
                                    }`}>
                                        {product.hasVariants ? `Min ${Math.min(...product.variants.map(v => v.stock))} Left` : `${product.stock} Left`}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
